<template>
  <div v-if="enabled" class="local_db_refresh_wrap">
    <v-tooltip bottom max-width="380">
      <template v-slot:activator="{ on, attrs }">
        <v-chip
          v-bind="attrs"
          :class="{ local_db_refresh: true, 'local_db_refresh--busy': busy }"
          :color="view.color"
          :link="canStart"
          :ripple="canStart"
          dark
          small
          v-on="on"
          @click="onChipClick"
        >
          <v-progress-circular v-if="busy" class="mr-2" color="white" indeterminate size="14" width="2" />
          <v-icon v-else left small>{{ view.icon }}</v-icon>
          {{ view.text }}
          <v-icon v-if="canStart" right small>mdi-refresh</v-icon>
        </v-chip>
      </template>
      <div v-for="(line, i) in view.details" :key="i">{{ line }}</div>
    </v-tooltip>

    <v-dialog v-model="confirm" max-width="460">
      <v-card>
        <v-card-title class="text-h6">Обновить локальную БД из прода?</v-card-title>
        <v-card-text>
          <p>
            Локальная БД agregatorus_local будет перезаписана снимком прода, это займёт около минуты. Перед
            записью скрипт сохранит бэкап текущей локальной БД.
          </p>
          <p class="mb-0">Локальные тестовые данные (слоты, тестовые схемы и т. п.) пропадут.</p>
          <p v-if="devDeployWindow" class="mt-3 mb-0 orange--text text--darken-3">
            Сейчас на сервере разворачивается дев из прода (06:55–07:10 по Москве): лучше подождать, чтобы не
            читать прод одновременно с этим.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirm = false">Отмена</v-btn>
          <v-btn :loading="starting" color="primary" text @click="start">Обновить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="notice.show" :color="notice.color" :timeout="10000" top>
      {{ notice.text }}
      <template v-slot:action="{ attrs }">
        <v-btn v-if="notice.reload" text v-bind="attrs" @click="reload">Перезагрузить</v-btn>
        <v-btn text v-bind="attrs" @click="notice.show = false">Закрыть</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
const POLL_MS = 60 * 1000;
// пока идёт обновление, спрашиваем состояние чаще — чтобы лоадер и этап были живыми
const FAST_POLL_MS = 2000;
const DAY_MS = 24 * 60 * 60 * 1000;

const time = (ms) =>
  new Date(ms).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
const dayMonth = (ms) =>
  new Date(ms).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

/**
 * Индикатор в шапке локальной админки: обновлялась ли локальная БД из прода сегодня, и кнопка «обновить сейчас».
 * Обновление делает scripts/refresh-local-db.sh (репозиторий бэкенда), его маркеры, блокировку и лог отдаёт
 * dev-сервер админки (vue.config.js, /__local-db-refresh), он же по клику запускает `run --force`
 * (POST /__local-db-refresh/run) и дальше следит за ходом: пока скрипт работает (в том числе запущенный
 * не отсюда), в чипе лоадер и текущий этап из лога. Показывается только при `npm run serve` и только если
 * админка смотрит в локальный бэкенд (VUE_APP_BASEURL_DEV = 127.0.0.1) — на деве и проде этого индикатора нет.
 * «Сегодня» — по часам этого же компьютера, как считает и сам скрипт.
 */
export default {
  name: "LocalDbRefreshStatus",
  data: () => ({
    // null — ответа ещё нет или dev-сервер не отдаёт состояние
    status: null,
    now: Date.now(),
    timer: null,
    confirm: false,
    // идёт запрос на запуск
    starting: false,
    // запуск из этой вкладки: что было до него, чтобы по завершении сказать, чем кончилось
    watch: null,
    notice: { show: false, text: "", color: "info", reload: false },
  }),
  computed: {
    enabled() {
      const base = this.$store.state.BASE_URL || "";
      return (
        process.env.NODE_ENV === "development" &&
        /^https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?(\/|$)/.test(base)
      );
    },
    running() {
      return !!(this.status && this.status.running);
    },
    busy() {
      return this.starting || this.running;
    },
    canStart() {
      return !!this.status && !this.busy;
    },
    // 06:55–07:10 по Москве на сервере пересоздаётся дев-БД из снапшота прода — в это время прод лучше не читать
    devDeployWindow() {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Moscow",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(new Date(this.now));
      const hour = Number(parts.find((p) => p.type === "hour").value) % 24;
      const minute = Number(parts.find((p) => p.type === "minute").value);
      const minutes = hour * 60 + minute;
      return minutes >= 6 * 60 + 55 && minutes <= 7 * 60 + 10;
    },
    view() {
      const s = this.status;

      if (this.busy) {
        const details = ["Идёт обновление локальной БД из прода (обычно около минуты)."];
        if (s && s.phase) details.push(`Сейчас: ${s.phase}`);
        if (s && s.runningSince) {
          details.push(`Идёт ${Math.max(0, Math.round((this.now - s.runningSince) / 1000))} с`);
        }
        return { color: "blue darken-1", icon: "", text: "БД обновляется…", details };
      }

      if (!s || !s.configured) {
        return {
          color: "grey darken-1",
          icon: "mdi-help-circle-outline",
          text: "БД: нет данных об обновлении",
          details: [
            s
              ? "Автообновление локальной БД не настроено: нет каталога состояния скрипта refresh-local-db.sh."
              : "Не удалось получить состояние обновления с dev-сервера админки (перезапустите его после обновления vue.config.js).",
          ].concat(s ? ["Нажмите, чтобы запустить обновление."] : []),
        };
      }

      const ok = s.lastSuccessAt;
      const failedAfter = s.lastFailureAt && (!ok || s.lastFailureAt > ok);
      const today = !!ok && sameDay(ok, this.now);

      const details = [];
      if (ok) {
        const days = Math.floor((this.now - ok) / DAY_MS);
        details.push(
          `Последнее успешное обновление: ${dayMonth(ok)} в ${time(ok)}` +
            (today ? " (сегодня)" : days > 0 ? ` (${days} дн. назад)` : "")
        );
      } else {
        details.push("Успешных обновлений ещё не было.");
      }
      if (failedAfter) {
        details.push(
          `Последняя попытка не удалась: ${dayMonth(s.lastFailureAt)} в ${time(s.lastFailureAt)}` +
            (s.lastFailureMessage ? ` — ${s.lastFailureMessage}` : "")
        );
      }
      details.push("Автообновление: раз в сутки, окно 07:00–09:00 по часам Mac, когда за Mac сидят.");
      details.push("Нажмите, чтобы обновить сейчас.");

      if (today) {
        return {
          color: "green darken-1",
          icon: "mdi-check-circle",
          text: `БД обновлена сегодня в ${time(ok)}`,
          details,
        };
      }
      return {
        color: failedAfter ? "red darken-2" : "orange darken-3",
        icon: "mdi-close-circle",
        text: ok ? `БД не обновлена · ${dayMonth(ok)} ${time(ok)}` : "БД не обновлялась",
        details,
      };
    },
  },
  mounted() {
    if (!this.enabled) return;
    this.refresh();
    document.addEventListener("visibilitychange", this.onVisible);
  },
  beforeDestroy() {
    clearTimeout(this.timer);
    document.removeEventListener("visibilitychange", this.onVisible);
  },
  methods: {
    onVisible() {
      if (!document.hidden) this.refresh();
    },
    // загрузить состояние и запланировать следующую проверку: чаще, пока идёт обновление
    async refresh() {
      await this.load();
      clearTimeout(this.timer);
      this.timer = setTimeout(this.refresh, this.busy ? FAST_POLL_MS : POLL_MS);
    },
    async load() {
      this.now = Date.now();
      try {
        const response = await fetch("/__local-db-refresh", { cache: "no-store" });
        this.status = response.ok ? await response.json() : null;
      } catch (e) {
        this.status = null;
      }
      this.checkFinished();
    },
    onChipClick() {
      if (!this.canStart) return;
      this.now = Date.now();
      this.confirm = true;
    },
    async start() {
      this.starting = true;
      const before = this.status || {};
      try {
        const response = await fetch("/__local-db-refresh/run", {
          method: "POST",
          // свой заголовок: чужая страница в браузере его без предварительного запроса (CORS) не отправит
          headers: { "X-Local-Db-Refresh": "1" },
        });
        if (response.status === 202) {
          this.watch = {
            success: before.lastSuccessAt || 0,
            failure: before.lastFailureAt || 0,
            startedAt: Date.now(),
            sawRunning: false,
          };
        } else if (response.status !== 409) {
          // 409 — обновление уже идёт (например, запущено не отсюда): просто покажем ход
          this.notify(await this.failureText(response), "error", false);
        }
      } catch (e) {
        this.notify("Не удалось связаться с dev-сервером админки.", "error", false);
      } finally {
        this.starting = false;
        this.confirm = false;
      }
      await this.refresh();
    },
    async failureText(response) {
      let body = {};
      try {
        body = await response.json();
      } catch (e) {
        body = {};
      }
      if (response.status === 403) {
        return "Запрос отклонён: откройте админку по адресу localhost или 127.0.0.1, а не по IP сети.";
      }
      if (body.error === "script_not_found") return `Не найден скрипт обновления: ${body.script}`;
      return `Не удалось запустить обновление (код ${response.status}).`;
    },
    // запуск из этой вкладки закончился — говорим, чем: успех, сбой с текстом или скрипт упал без маркера
    checkFinished() {
      const w = this.watch;
      if (!w || !this.status) return;
      if (this.running) {
        w.sawRunning = true;
        return;
      }
      // скрипт мог отработать (или упасть) быстрее, чем мы успели увидеть «идёт»
      if (!w.sawRunning && Date.now() - w.startedAt < 5000) return;
      this.watch = null;

      const s = this.status;
      const run = s.manualRun;
      if (s.lastSuccessAt && s.lastSuccessAt > w.success) {
        this.notify("Локальная БД обновлена из прода. Чтобы увидеть свежие данные, перезагрузите страницу.", "success", true);
      } else if (s.lastFailureAt && s.lastFailureAt > w.failure) {
        this.notify(`Обновление не удалось: ${s.lastFailureMessage || "причина в логе скрипта"}`, "error", false);
      } else if (run && (run.error || run.exitCode)) {
        const code = run.exitCode === null || run.exitCode === undefined ? "?" : run.exitCode;
        this.notify(
          `Скрипт обновления завершился с ошибкой (код ${code}${run.error ? `, ${run.error}` : ""}). Лог: ~/Library/Logs/agregatorus-db-refresh/`,
          "error",
          false
        );
      } else {
        this.notify("Обновление завершилось, но маркер успеха не изменился — проверьте лог скрипта.", "warning", false);
      }
    },
    notify(text, color, reload) {
      this.notice = { show: true, text, color, reload };
    },
    reload() {
      window.location.reload();
    },
  },
};
</script>

<style lang="scss" scoped>
.local_db_refresh_wrap {
  display: flex;
  align-items: center;
}
.local_db_refresh {
  margin-right: 8px;
  &--busy {
    cursor: default !important;
  }
}
</style>
